/**
 * Utility functions to transform video template configurations
 * into formats compatible with TextBrandingService.applyTemplate()
 */

import { datoColorToRgba } from '@src/utility/color';

import type { KaraokeTemplate } from '@src/services/karaoke/types';
import type { DatoColor, KaraokeTextBranding } from '@src/types.ts/story';

/**
 * Converts a DatoColor object to a CSS rgba string.
 * The Template type expects color values as strings, not DatoColor objects.
 *
 * @param color - DatoColor object from DatoCMS
 * @returns CSS rgba string, or null when there is no color
 */
function datoColorToString(color: DatoColor | null | undefined): string | null {
  if (!color) return null;
  return datoColorToRgba(color);
}

/**
 * Transforms a KaraokeTextBranding configuration from DatoCMS
 * into a KaraokeTemplate format compatible with TextBrandingService.applyTemplate().
 *
 * This function:
 * 1. Converts all DatoColor objects to CSS color strings
 * 2. Ensures the 'type' field is set to 'karaoke'
 * 3. Generates an ID if one doesn't exist
 * 4. Preserves all other properties as-is
 *
 * @param captionsConfig - The karaoke configuration from video template
 * @returns KaraokeTemplate object ready for applyTemplate()
 */
export function transformKaraokeConfigToTemplate(
  captionsConfig: KaraokeTextBranding | null,
): KaraokeTemplate | null {
  if (!captionsConfig) return null;

  // Convert color objects to strings
  const fillColor = datoColorToString(captionsConfig.fillColor);
  const backgroundColor = datoColorToString(captionsConfig.backgroundColor);
  const strokeColor = datoColorToString(captionsConfig.strokeColor);
  const shadowColor = datoColorToString(captionsConfig.shadowColor);
  const transcriptColor = captionsConfig.transcriptColor
    ? datoColorToString(captionsConfig.transcriptColor)
    : undefined;

  // Create the template object
  // KaraokeTemplate type is: Record<keyof KaraokeTextBranding, string | number | boolean | null>
  const template: KaraokeTemplate = {
    // Ensure required fields
    id: captionsConfig.id || `karaoke-${Date.now()}`,
    type: 'karaoke',

    // Generic text branding properties (from GenericTextBranding)
    title: captionsConfig.title,
    fontFamily: captionsConfig.fontFamily,
    fontSize: captionsConfig.fontSize,
    fillColor: fillColor as any, // Template expects string, DatoColor is incompatible
    backgroundColor: backgroundColor as any,
    backgroundTransparency: captionsConfig.backgroundTransparency,
    backgroundAlignThreshold: captionsConfig.backgroundAlignThreshold,
    backgroundBorderRadius: captionsConfig.backgroundBorderRadius,
    fontWeight: captionsConfig.fontWeight,
    xAlignment: captionsConfig.xAlignment,
    italic: captionsConfig.italic,
    uppercase: captionsConfig.uppercase,
    aspectRatio: captionsConfig.aspectRatio,
    shadowColor: shadowColor as any,
    shadowBlur: captionsConfig.shadowBlur,
    shadowXOffset: captionsConfig.shadowXOffset,
    shadowYOffset: captionsConfig.shadowYOffset,

    // Karaoke-specific properties (from KaraokeTextBranding)
    animation: captionsConfig.animation,
    xPos: captionsConfig.xPos,
    yPos: captionsConfig.yPos,
    instagramLines: captionsConfig.instagramLines,
    strokeWidth: captionsConfig.strokeWidth,
    strokeColor: strokeColor as any,
    letterSpacing: captionsConfig.letterSpacing,
    hideComma: captionsConfig.hideComma,
    hidePeriod: captionsConfig.hidePeriod,
    hideFillers: captionsConfig.hideFillers,

    // Optional karaoke properties
    transcriptEffect: captionsConfig.transcriptEffect || null,
    transcriptSplit: captionsConfig.transcriptSplit || null,
    transcriptPlacement: captionsConfig.transcriptPlacement || null,
    transcriptMaximumLength: captionsConfig.transcriptMaximumLength || null,
    lineBreakStyle: captionsConfig.lineBreakStyle || null,
    transcriptColor: transcriptColor || null,
    presetId: captionsConfig.presetId || null,
    previewText: captionsConfig.previewText || null,
  };

  return template;
}
